import {
  deAT,
  enGB,
  frCH,
  ro,
  sv,
  itCH,
  de,
  nb,
  fi,
  bg,
  cs,
  lb,
  srLatn,
  fr,
} from 'date-fns/locale'

import { DEFAULT_LOCALE } from '@ngb-frontend/shared/config'

import type { AppLocale } from '@ngb-frontend/shared/types'
import type { Locale as DateFnsLocale } from 'date-fns/locale'

export const DEFAULT_DATE_FNS_LOCALE = enGB

// Keep the number/date formatting of the country the app locale belongs to
// (formatLong + options) and borrow only the content strings (month/day names,
// relative/distance phrasing, parsing) from the language locale. Used when no
// country-specific date-fns locale exists for a language, e.g. French content
// for Luxembourg should keep Luxembourg's formatting rules, not France's.
const withContentFrom = (
  formatting: DateFnsLocale,
  content: DateFnsLocale,
  code: AppLocale,
): DateFnsLocale => ({
  ...formatting,
  code,
  localize: content.localize,
  match: content.match,
  formatDistance: content.formatDistance,
  formatRelative: content.formatRelative,
})

// Replace a locale's short date pattern, keeping the other widths as the
// locale defines them. Overriding the locale rather than each call site keeps
// one source of truth: every reader of formatLong.date({ width: 'short' })
// agrees, this app's own formatters and Velocity's DatePicker, which builds
// its input mask and placeholder from exactly that call.
const withShortDateFormat = (
  locale: DateFnsLocale,
  shortDate: string,
): DateFnsLocale => ({
  ...locale,
  formatLong: {
    ...locale.formatLong,
    date: (options) =>
      options.width === 'short' ? shortDate : locale.formatLong.date(options),
  },
})

// Numeric dates are written day first with a four digit year and with the
// same separator in every country, so that a date-fns locale update cannot
// move one country's dates on its own and no screen leads with the year
// (date-fns writes Swedish short dates as y-MM-dd, Serbian as "dd. MM. yy.").
const DEFAULT_DATE_FORMAT = 'dd/MM/yyyy'

// The one country that writes numeric dates differently.
const DATE_FORMAT_OVERRIDES: Partial<Record<AppLocale, string>> = {
  'bg-BG': 'dd-MM-yyyy',
  'en-BG': 'dd-MM-yyyy',
}

// English locales: keep each country's formatting, use English content
const enSE = withContentFrom(sv, enGB, 'en-SE')
const enAT = withContentFrom(deAT, enGB, 'en-AT')
const enRO = withContentFrom(ro, enGB, 'en-RO')
const enCH = withContentFrom(frCH, enGB, 'en-CH')
const enNO = withContentFrom(nb, enGB, 'en-NO')
const enBG = withContentFrom(bg, enGB, 'en-BG')
const enCZ = withContentFrom(cs, enGB, 'en-CZ')
const enFI = withContentFrom(fi, enGB, 'en-FI')
const enLU = withContentFrom(lb, enGB, 'en-LU')
const enRS = withContentFrom(srLatn, enGB, 'en-RS')

// Non-English locales whose language has no country-specific date-fns locale:
// keep the country's formatting, borrow only the language's content strings
const frLU = withContentFrom(lb, fr, 'fr-LU')
const deLU = withContentFrom(lb, de, 'de-LU')
const deCH = withContentFrom(frCH, de, 'de-CH')

// TODO: lazy load formatting locales
const baseLocales: Partial<Record<AppLocale, DateFnsLocale>> = {
  // Prefer country specific language locales (if they exist), else keep the
  // country's formatting and only borrow the language's content strings
  [DEFAULT_LOCALE]: DEFAULT_DATE_FNS_LOCALE,
  'en-GB': enGB,
  'sv-SE': sv,
  'en-SE': enSE,
  'de-AT': deAT,
  'en-AT': enAT,
  'ro-RO': ro,
  'en-RO': enRO,
  'fr-CH': frCH,
  'de-CH': deCH,
  'it-CH': itCH,
  'en-CH': enCH,
  'en-NO': enNO,
  'nb-NO': nb,
  'en-BG': enBG,
  'en-CZ': enCZ,
  'fi-FI': fi,
  'en-FI': enFI,
  'fr-LU': frLU,
  'de-LU': deLU,
  'en-LU': enLU,
  'bg-BG': bg,
  'cs-CZ': cs,
  'sr-RS': srLatn,
  'en-RS': enRS,
}

// One numeric date pattern per locale, applied to the locale itself so that
// everything reading formatLong.date({ width: 'short' }) agrees: this app's
// own formatters, and Velocity's DatePicker, which builds its input mask and
// its placeholder from exactly that call.
export const dateFnsLocales: Partial<Record<AppLocale, DateFnsLocale>> = (
  Object.keys(baseLocales) as AppLocale[]
).reduce<Partial<Record<AppLocale, DateFnsLocale>>>((acc, code) => {
  const locale = baseLocales[code]

  if (locale) {
    acc[code] = withShortDateFormat(
      locale,
      DATE_FORMAT_OVERRIDES[code] ?? DEFAULT_DATE_FORMAT,
    )
  }

  return acc
}, {})
